import {FC, useCallback, useEffect, useRef, useState} from 'react'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {useUserProfileContext} from '../../../context/UserProfileContext'
import {useHistory} from 'react-router-dom'
import { defaultProfileImage } from '../../../utils/constants'
import DOMPurify from "dompurify";

// Umbral en px: por debajo de este ancho ocultamos flechas y dejamos solo
// swipe tactil (opcion 1 confirmada por Armando). Usamos 992 para alinearnos
// con el breakpoint Bootstrap "lg" que ya usan otras secciones del dashboard.
const ARROWS_MIN_WIDTH_PX = 992
// Cuanto avanza el carrusel por click en una flecha. 310 = ancho card (290)
// + gap (20) definidos en _nb-member-search.scss.
const SCROLL_STEP_PX = 310

interface Member {
  id: string
  name: string
  badge: string
  categories: string[]
  description: string
  location: string
  image: string
}

interface MemberSearchListProps {
  data: Member[]
  loading: boolean
  // Optional override so pages like /expert-search can send the click to a different route
  // (e.g. the expert public profile) instead of the default /member/overview navigation.
  onItemClick?: (id: string) => void
}

const MemberSearchList: FC<MemberSearchListProps> = ({data = [], loading, onItemClick}) => {
  const {setSearchParams} = useUserProfileContext()
  const navigate = useHistory()

  const listRef = useRef<HTMLDivElement>(null)
  // arrowsEnabled: gate a "hay flechas" segun viewport. En mobile/tablet
  // (< ARROWS_MIN_WIDTH_PX) el usuario navega con swipe tactil nativo.
  const [arrowsEnabled, setArrowsEnabled] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= ARROWS_MIN_WIDTH_PX : true,
  )
  // canPrev/canNext esconden la flecha correspondiente cuando ya estamos en
  // ese extremo del scroll: evita ver un boton que no haria nada.
  const [canPrev, setCanPrev] = useState(false)
  const [canNext, setCanNext] = useState(false)

  const updateEdges = useCallback(() => {
    const el = listRef.current
    if (!el) return
    const {scrollLeft, scrollWidth, clientWidth} = el
    // Tolerancia de 2px: los navegadores redondean scrollLeft con decimales
    // en zoom no-100% y sin esto una flecha quedaria "encendida" al tope.
    setCanPrev(scrollLeft > 2)
    setCanNext(scrollLeft + clientWidth < scrollWidth - 2)
  }, [])

  useEffect(() => {
    // Recalcula al resize (viewport cruzando el breakpoint, cambio de zoom,
    // etc.) y al primer render post-data.
    const onResize = () => {
      setArrowsEnabled(window.innerWidth >= ARROWS_MIN_WIDTH_PX)
      updateEdges()
    }
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [updateEdges])

  useEffect(() => {
    // Data recien cargada: hay que recalcular edges cuando las cards estan
    // ya montadas y el contenedor conoce su scrollWidth real.
    updateEdges()
  }, [data, updateEdges])

  const scrollBy = (offset: number) => {
    const el = listRef.current
    if (!el) return
    el.scrollBy({left: offset, behavior: 'smooth'})
  }

  if (loading) return <div className='members-page__no-results'>Loading...</div>
  if (!data?.length) return <div className='members-page__no-results'>No results found</div>

  return (
    <>
      <div className='members-page__members-carousel'>
        {arrowsEnabled && canPrev && (
          <button
            type='button'
            aria-label='Previous members'
            className='members-page__members-arrow members-page__members-arrow--prev'
            onClick={() => scrollBy(-SCROLL_STEP_PX)}
          >
            <svg width='10' height='16' viewBox='0 0 10 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path d='M8.5 1L1.5 8L8.5 15' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
            </svg>
          </button>
        )}
        {arrowsEnabled && canNext && (
          <button
            type='button'
            aria-label='Next members'
            className='members-page__members-arrow members-page__members-arrow--next'
            onClick={() => scrollBy(SCROLL_STEP_PX)}
          >
            <svg width='10' height='16' viewBox='0 0 10 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path d='M1.5 1L8.5 8L1.5 15' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
            </svg>
          </button>
        )}
      <div
        className='members-page__members-list'
        ref={listRef}
        onScroll={updateEdges}
      >
        {data.map((member) => (
          <div
            key={member.id}
            className='members-page__member-card'
            onClick={() => {
              if (onItemClick) {
                onItemClick(member.id)
                return
              }
              setSearchParams({userSelected: member.id})
              navigate.push('/member/overview')
            }}
          >
            <img src={toAbsoluteUrl(member.image)} alt={member.name} className='members-page__member-image' onError={(e)=>{e.currentTarget.src=defaultProfileImage}}
            />

            {/* Overlay default */}
            <div className='members-page__member-overlay-default'>
              {/* PR-I-79: el backend devuelve badge vacio cuando el miembro ya
                  supero los 90 dias desde date_joined -> ocultamos la leyenda
                  y los dashes decorativos para no dejar una linea suelta. */}
              {member.badge && (
                <div className='members-page__member-badge'>
                  <svg
                    width='14'
                    height='1'
                    viewBox='0 0 14 1'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path d='M0 0.25H13.5' stroke='white' stroke-width='0.5' />
                  </svg>
                  {member.badge}
                  <svg
                    width='14'
                    height='1'
                    viewBox='0 0 14 1'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path d='M0 0.25H13.5' stroke='white' stroke-width='0.5' />
                  </svg>
                </div>
              )}
              <div className='members-page__member-name'>{member.name}</div>
            </div>

            {/* Overlay hover */}
            <div className='members-page__member-overlay-hover'>
              <div>
                {/* PR-I-79: mismo criterio que el overlay default. */}
                {member.badge && (
                  <div className='members-page__member-badge'>
                    <svg
                      width='14'
                      height='1'
                      viewBox='0 0 14 1'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path d='M0 0.25H13.5' stroke='white' stroke-width='0.5' />
                    </svg>
                    {member.badge}
                    <svg
                      width='14'
                      height='1'
                      viewBox='0 0 14 1'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path d='M0 0.25H13.5' stroke='white' stroke-width='0.5' />
                    </svg>
                  </div>
                )}
                <div className='members-page__member-name'>{member.name}</div>
                <div className='members-page__member-categories'>
                  {member.categories.map((cat, idx) => (
                    <div key={idx} className='members-page__member-category'>
                      {cat}
                    </div>
                  ))}
                </div>
              </div>
              {/* <div className='members-page__member-description' dangerouslySetInnerHTML={{ __html: (DOMPurify.sanitize(member.description || '')) }}></div> */}
              <div className='members-page__member-location'>
                <div className='members-page__member-location-icon'>
                  <svg
                    width='14'
                    height='14'
                    viewBox='0 0 14 14'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M7.00018 7.83334C8.00532 7.83334 8.82018 7.01854 8.82018 6.01334C8.82018 5.0082 8.00532 4.19336 7.00018 4.19336C5.99503 4.19336 5.18018 5.0082 5.18018 6.01334C5.18018 7.01854 5.99503 7.83334 7.00018 7.83334Z'
                      stroke='white'
                    />
                    <path
                      d='M2.11182 4.95185C3.26099 -0.0998163 10.7451 -0.0939823 11.8885 4.95768C12.5593 7.92102 10.716 10.4293 9.10015 11.981C7.92765 13.1127 6.07265 13.1127 4.89432 11.981C3.28432 10.4293 1.44099 7.91518 2.11182 4.95185Z'
                      stroke='white'
                    />
                  </svg>
                </div>
                <div className='members-page__member-location-text'>{member.location}</div>
              </div>
              <div className='members-page__member-learn-more'>
                <a
                  className='members-page__member-learn-more-text'
                  onClick={() => {
                    setSearchParams({userSelected: member.id})
                  }}
                >
                  Learn More
                </a>
                <svg
                  width='14'
                  height='4'
                  viewBox='0 0 14 4'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path d='M8.5 0.378906L12 3.38306H0' stroke='white' />
                </svg>
              </div>
            </div>
          </div>
        ))}
      </div>
      </div>
    </>
  )
}

export {MemberSearchList}
